import { differenceInDays } from "date-fns"
import { useMemo, useState } from "react"
import { isMobile } from "react-device-detect"
import { Link } from "react-router-dom"
import styled from "styled-components"
import { IllustrationCreateLesson } from "~/assets/IllustrationCreateLesson"
import { LargeButton, LargeOutlineButton } from "~/components"
import { captureEvent } from "~/modules/analytics/capture"
import { useTranslate } from "~/ur-react-hooks"
import { useUser } from "~/utils"
import { useOrganizationMembership } from "~/utils/hooks/useOrganizationMembership"
import { useSubscriptionInfo } from "~/utils/hooks/useSubscriptionInfo"
import DashboardCreateWorksheetMenu from "./DashboardCreateWorksheetMenu"
import DashboardSubscriptionExpiringBanner from "./DashboardSubscriptionExpiringBanner"

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: center;

  width: 100%;
  height: 100%;

  // For the expiration banner to be contained properly
  position: relative;
`

const InnerWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: center;
  /* margin-left: -128px; */
  margin: 16px;
  margin-top: 32px;

  width: calc(100% - 32px);
  height: 100%;
`

const Title = styled.div`
  font-style: normal;
  font-weight: 500;
  line-height: 43px;
  font-size: 32px;
  letter-spacing: 0;
  text-align: center;
`

const Text = styled.div`
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0;
  text-align: center;

  padding: 32px 0;
`

const IllustrationWrapper = styled.div`
  width: 433px;
  max-width: 800px;
`

const Buttons = styled.div`
  display: flex;
  gap: 1.5rem;
  flex-direction: row;
`

const CreateButtonWrapper = styled.div`
  // Required as a we have a submenu that is absolute positioned
  position: relative;
`

type DashboardProps = {}

export const Dashboard: React.FC<DashboardProps> = () => {
  const user = useUser()
  const organization = useOrganizationMembership()
  const isAdminOfOrganization =
    organization?.role === "ADMIN" || organization?.role === "OWNER"

  const subscription = useSubscriptionInfo()

  const translations = useTranslate({
    welcome: "common.welcome",
    createWorksheet: "common.create-worksheet",
    addUser: "dashboard.add-user",
    welcomeTextOne: "dashboard.welcome-text-one",
    welcomeTextTwo: "dashboard.welcome-text-two",
  })

  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const showTrialExpiredBanner = useMemo(() => {
    if (!subscription) return false

    if (user.hasActivePaymentMethod) {
      return false
    }

    const trialEnd = subscription.trialEnd

    if (!trialEnd) return false

    const daysLeft = differenceInDays(trialEnd, new Date())

    return daysLeft <= 3
  }, [user, subscription])

  return (
    <Wrapper>
      {showTrialExpiredBanner && (
        <DashboardSubscriptionExpiringBanner subscription={subscription!} />
      )}
      <InnerWrapper>
        <IllustrationWrapper>
          <IllustrationCreateLesson />
        </IllustrationWrapper>
        <Title>
          {translations.welcome}
          {user?.firstName ? `, ${user.firstName}!` : "!"}
        </Title>

        <Text>
          {translations.welcomeTextOne}
          {isMobile ? " " : <br />}
          {translations.welcomeTextTwo}
        </Text>

        <Buttons>
          {isAdminOfOrganization ? (
            <>
              <Link to="/users/add">
                <LargeButton leftIcon="plus">
                  {translations.addUser}
                </LargeButton>
              </Link>
              <CreateButtonWrapper>
                {isMenuOpen && (
                  <DashboardCreateWorksheetMenu
                    onClose={() => {
                      captureEvent("close_new_worksheet_menu")
                      setIsMenuOpen(false)
                    }}
                  />
                )}
                <LargeOutlineButton
                  leftIcon="plus"
                  onClick={() => {
                    captureEvent("open_new_worksheet_menu", {
                      variant: "admin",
                    })
                    setIsMenuOpen(true)
                  }}
                >
                  {translations.createWorksheet}
                </LargeOutlineButton>
              </CreateButtonWrapper>
            </>
          ) : (
            <CreateButtonWrapper>
              {isMenuOpen && (
                <DashboardCreateWorksheetMenu
                  onClose={() => {
                    captureEvent("close_new_worksheet_menu")
                    setIsMenuOpen(false)
                  }}
                />
              )}
              <LargeButton
                leftIcon="plus"
                onClick={() => {
                  captureEvent("open_new_worksheet_menu", {
                    variant: "user",
                  })
                  setIsMenuOpen(true)
                }}
              >
                {translations.createWorksheet}
              </LargeButton>
            </CreateButtonWrapper>
          )}
        </Buttons>
      </InnerWrapper>
    </Wrapper>
  )
}
